import { useQuery, useQueryClient } from '@tanstack/react-query';
import { API, graphqlOperation } from 'aws-amplify';
import { getLinkedDevices } from 'src/graphql/queries';
import {
  GetLinkedDevicesInput,
  GetLinkedDevicesQuery,
  LinkedDevice,
} from 'src/API';
import { GraphQLResult } from '@aws-amplify/api-graphql';
import { log } from 'src/utils/helpers';
import { useAppSelector } from 'src/stores/slices/hooks';
import {
  Resource,
  ResourceAction,
  useHasResourcePermission,
} from '@hooks/useHasResourcePermission';
import { selectSelectedSiteCodes } from 'src/stores/slices/userPrefsSlice';
import { useSitesWithCodes } from 'src/hooks/useSitesWithCodes';

async function fetchLinkedDevices(
  sites: GetLinkedDevicesInput[]
): Promise<LinkedDevice[]> {
  if (!sites) {
    return [];
  }

  try {
    const response = (await API.graphql<GetLinkedDevicesQuery>(
      graphqlOperation(getLinkedDevices, { sites })
    )) as GraphQLResult<GetLinkedDevicesQuery>;

    if (!response.data || !response.data.getLinkedDevices) {
      return [];
    }

    return response.data.getLinkedDevices.data?.filter(
      Boolean
    ) as LinkedDevice[];
  } catch (error) {
    log('Error getting sites', true, { error });
    throw error;
  }
}

export function useLinkedDevices() {
  const selectedSiteCodes = useAppSelector(selectSelectedSiteCodes);
  const { sitesWithCodes } = useSitesWithCodes();

  const { hasPermission: canReadLinkedDevices } = useHasResourcePermission(
    Resource.DeviceLink,
    ResourceAction.Read
  );

  const query = useQuery({
    queryKey: ['linkedDevices', selectedSiteCodes],
    queryFn: () => fetchLinkedDevices(sitesWithCodes || []),
    enabled: !!selectedSiteCodes && canReadLinkedDevices,
    retry: (failureCount, error) => {
      // Don't retry if we get a 403 (forbidden)
      if (error instanceof Error && error.message.includes('403')) {
        return false;
      }

      return failureCount < 4;
    },
    retryDelay: attemptIndex => 1000 * attemptIndex,
  });

  return {
    data: query.data,
    isError: query.isError,
    // If there is no selectedSites
    isLoading: query.isFetching || query.isLoading || !selectedSiteCodes,
    isForbidden: !canReadLinkedDevices,
    refetch: query.refetch,
  };
}
